import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Pallets } from '../../../interfaces/Pallets.interfaces';
import { Dealer } from '../../../interfaces/waybill.interfaces';
import { PalletLabelService } from '../../../services/pallet-label.service';
import { NotificationService } from '../../../services/notification.service';
import { ReturnRequestService } from '../../../services/return-request.service';

/** Bolla ricavata dai pallet creati: codice + pallet che la compongono. */
export interface CreatedWaybillSummary {
  waybillCode: string;
  pallets: Pallets[];
}

/**
 * Popup di riepilogo della creazione resi: mostra l'elenco dei pallet creati
 * e quello delle bolle generate (raggruppate per palletWaybillCode), poi le
 * due card di stampa multipla (label e bolle).
 * Stesso pattern overlay/stile di return-request-print-popup.
 */
@Component({
  selector: 'app-return-request-summary-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './return-request-summary-popup.component.html',
  styleUrl: './return-request-summary-popup.component.css'
})
export class ReturnRequestSummaryPopupComponent implements OnInit, OnDestroy {
  @Input() visible: boolean = false;
  /** Pallet restituiti dal backend alla creazione (POST /create). */
  @Input() set pallets(value: Pallets[] | null) {
    this.createdPallets = (value || []).filter((p) => !!p);
    this.waybills = this.groupByWaybill(this.createdPallets);
  }
  /** Anagrafica del dealer mittente del reso (per i dati della label). */
  @Input() dealer: Dealer | null = null;
  @Output() closed = new EventEmitter<void>();

  /** Pallet creati, nell'ordine restituito dal backend. */
  createdPallets: Pallets[] = [];
  /** Bolle create: distinct di palletWaybillCode con i relativi pallet. */
  waybills: CreatedWaybillSummary[] = [];

  /** True mentre il PDF con le label è in generazione (disabilita il bottone). */
  isGeneratingLabels = false;
  /** True mentre il backend genera il PDF delle bolle. */
  isGeneratingWaybills = false;

  constructor(
    private palletLabelService: PalletLabelService,
    private notificationService: NotificationService,
    private returnRequestService: ReturnRequestService
  ) {}

  ngOnInit(): void {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.visible) {
      this.close();
    }
  };

  close(): void {
    this.closed.emit();
  }

  trackByPalletCode(index: number, pallet: Pallets): string {
    return pallet.palletCode || String(index);
  }

  trackByWaybillCode(index: number, waybill: CreatedWaybillSummary): string {
    return waybill.waybillCode || String(index);
  }

  /**
   * Raggruppa i pallet creati per codice bolla mantenendo l'ordine di arrivo:
   * il backend assegna lo stesso palletWaybillCode a tutti i pallet della
   * stessa richiesta, ma una creazione multipla può generarne più di uno.
   */
  private groupByWaybill(pallets: Pallets[]): CreatedWaybillSummary[] {
    const byCode = new Map<string, CreatedWaybillSummary>();
    pallets.forEach((pallet) => {
      const code = (pallet.palletWaybillCode || '').trim();
      if (!code) {
        return;
      }
      const existing = byCode.get(code);
      if (existing) {
        existing.pallets.push(pallet);
        return;
      }
      byCode.set(code, { waybillCode: code, pallets: [pallet] });
    });
    return Array.from(byCode.values());
  }

  /**
   * Stampa multipla: un unico PDF con la label di ogni pallet elencato.
   */
  printLabels(): void {
    const printable = this.createdPallets.filter((p) => !!p.palletCode);
    if (!printable.length) {
      this.notificationService.error('Nessun codice reso disponibile: impossibile generare le label');
      return;
    }

    this.isGeneratingLabels = true;
    this.palletLabelService.generatePalletLabels(printable, this.dealer).subscribe({
      next: (success) => {
        this.isGeneratingLabels = false;
        if (success) {
          this.notificationService.success(printable.length === 1
            ? `Label generata per il reso ${printable[0].palletCode}`
            : `${printable.length} label generate`);
        } else {
          this.notificationService.error('Errore nella generazione delle label');
        }
      },
      error: (err) => {
        console.error('Errore generazione label pallet:', err);
        this.isGeneratingLabels = false;
        this.notificationService.error('Errore nella generazione delle label');
      }
    });
  }

  /**
   * Stampa multipla delle bolle elencate: il backend restituisce un unico PDF
   * con tutte le bolle richieste (POST /return-request/waybills-print).
   */
  printWaybills(): void {
    const codes = this.waybills.map((w) => w.waybillCode).filter((code) => !!code);
    if (!codes.length) {
      this.notificationService.error('Nessun codice bolla disponibile: impossibile generare le bolle');
      return;
    }

    this.isGeneratingWaybills = true;
    this.returnRequestService.printWaybills(codes).subscribe({
      next: (pdf: Blob) => {
        this.isGeneratingWaybills = false;
        this.downloadPdf(pdf, codes);
        this.notificationService.success(codes.length === 1
          ? `Bolla ${codes[0]} generata`
          : `${codes.length} bolle generate`);
      },
      error: (err) => {
        console.error('Errore stampa bolle di reso:', err);
        this.isGeneratingWaybills = false;
        this.notificationService.error(err?.status === 404
          ? 'Nessuna bolla trovata per i codici indicati'
          : 'Errore nella generazione delle bolle');
      }
    });
  }

  /** Scarica il PDF restituito dal backend creando un link temporaneo. */
  private downloadPdf(pdf: Blob, codes: string[]): void {
    const url = window.URL.createObjectURL(pdf);
    const link = document.createElement('a');
    link.href = url;
    link.download = codes.length === 1
      ? `Bolla_${codes[0]}.pdf`
      : `Bolle_${codes.length}_resi.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
